import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Badge, Alert, Card, Row, Col } from 'react-bootstrap';
import VodService from '../../../services/VodService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const MediaDirectoryManager = () => {
  const [directories, setDirectories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  const [selectedDirectory, setSelectedDirectory] = useState(null);
  const [directoryAssets, setDirectoryAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    path: ''
  });

  useEffect(() => {
    fetchDirectories();
  }, []);

  const fetchDirectories = async () => {
    try {
      setLoading(true);
      const response = await VodService.getAllDirectories();
      if (response.success) {
        setDirectories(response.directories || []);
      }
    } catch (error) {
      toast.error('Failed to fetch directories');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDirectory = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await VodService.createDirectory(formData);
      if (response.success) {
        toast.success('Directory created successfully');
        setShowModal(false);
        setFormData({ name: '', description: '', path: '' });
        fetchDirectories();
      }
    } catch (error) {
      toast.error('Failed to create directory');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDirectory = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await VodService.updateDirectory(selectedDirectory.id, formData);
      if (response.success) {
        toast.success('Directory updated successfully');
        setShowEditModal(false);
        setSelectedDirectory(null);
        setFormData({ name: '', description: '', path: '' });
        fetchDirectories();
      }
    } catch (error) {
      toast.error('Failed to update directory');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDirectory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this directory? All assets in this directory will also be deleted.')) {
      return;
    }
    try {
      setLoading(true);
      const response = await VodService.deleteDirectory(id);
      if (response.success) {
        toast.success('Directory deleted successfully');
        fetchDirectories();
      }
    } catch (error) {
      toast.error('Failed to delete directory');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const viewDirectoryAssets = async (directory) => {
    try {
      setLoading(true);
      setSelectedDirectory(directory);
      const response = await VodService.getAssetsByDirectory(directory.id);
      if (response.success) {
        setDirectoryAssets(response.assets || []);
        setShowAssetsModal(true);
      }
    } catch (error) {
      toast.error('Failed to fetch directory assets');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (directory) => {
    setSelectedDirectory(directory);
    setFormData({
      name: directory.name,
      description: directory.description || '',
      path: directory.path || ''
    });
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setFormData({ name: '', description: '', path: '' });
    setShowModal(true);
  };

  return (
    <div className="media-directory-manager">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Media Directory Management</h2>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="flaticon-plus mr-2"></i> Create Directory
        </Button>
      </div>

      <Alert variant="info">
        <i className="flaticon-information mr-2"></i>
        Media directories help you organize your VOD content. Create directories to group related media files together.
      </Alert>

      <Row>
        {directories.map((directory) => (
          <Col lg={4} md={6} key={directory.id} className="mb-4">
            <Card className="directory-card h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{directory.name}</h5>
                <Badge variant="primary">{directory.asset_count || 0} files</Badge>
              </Card.Header>
              <Card.Body>
                <p className="text-muted">{directory.description || 'No description'}</p>
                <p className="small text-muted">
                  <i className="flaticon-folder mr-1"></i> {directory.path}
                </p>
              </Card.Body>
              <Card.Footer className="d-flex justify-content-between">
                <Button 
                  variant="outline-info" 
                  size="sm"
                  onClick={() => viewDirectoryAssets(directory)}
                >
                  <i className="flaticon-view mr-1"></i> View Assets
                </Button>
                <div>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    className="mr-2"
                    onClick={() => openEditModal(directory)}
                  >
                    <i className="flaticon-edit"></i>
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => handleDeleteDirectory(directory.id)}
                  >
                    <i className="flaticon-delete"></i>
                  </Button>
                </div>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>

      {directories.length === 0 && !loading && (
        <div className="text-center py-5">
          <i className="flaticon-folder" style={{ fontSize: '48px', color: '#ccc' }}></i>
          <p className="mt-3 text-muted">No directories found. Create your first directory to get started.</p>
        </div>
      )}

      {/* Create Directory Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Media Directory</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateDirectory}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Directory Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter directory name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter directory description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Path (optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., /uploads/vod/movies"
                value={formData.path}
                onChange={(e) => setFormData({...formData, path: e.target.value})}
              />
              <Form.Text className="text-muted">
                Leave empty for auto-generated path
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Directory'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit Directory Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Media Directory</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleUpdateDirectory}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Directory Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter directory name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter directory description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Path</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., /uploads/vod/movies"
                value={formData.path}
                onChange={(e) => setFormData({...formData, path: e.target.value})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Directory'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* View Assets Modal */}
      <Modal show={showAssetsModal} onHide={() => setShowAssetsModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Assets in {selectedDirectory?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {directoryAssets.length === 0 ? (
            <Alert variant="info">No assets in this directory yet.</Alert>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Duration</th>
                  <th>Size</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {directoryAssets.map((asset) => (
                  <tr key={asset.id}>
                    <td>{asset.title}</td>
                    <td>{Math.floor(asset.duration / 60)}:{(asset.duration % 60).toString().padStart(2, '0')}</td>
                    <td>{(asset.file_size / (1024 * 1024)).toFixed(2)} MB</td>
                    <td>
                      <Badge variant={asset.status === 'active' ? 'success' : 'secondary'}>
                        {asset.status}
                      </Badge>
                    </td>
                    <td>{new Date(asset.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssetsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MediaDirectoryManager;
